import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { url } from "../../utilis/contant";
import api from "../../utilis/api";

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useSelector((state) => state.auth);
  const sessionId = searchParams.get("session_id");

  const [status, setStatus] = useState("verifying"); // verifying | success | error | duplicate
  const [booking, setBooking] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setErrorMsg("No session ID found.");
      return;
    }

    const verify = async () => {
      try {
        const res = await api.post(
          "/payments/verify-lesson-payment",
          {
            sessionId,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = res.data;

        console.log("✅ Verify response:", data);

        if (data.success) {
          setBooking(data.data);
          setStatus(
            data.message === "Already booked." ? "duplicate" : "success",
          );
        } else {
          setStatus("error");
          setErrorMsg(data.message || "Verification failed.");
        }
      } catch (err) {
        console.error("❌ Verify error:", err);
        setStatus("error");
        setErrorMsg(err.response?.data?.message || err.message);
      }
    };

    verify();
  }, [sessionId, token]);

  // ---- UI ----

  if (status === "verifying") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner} />
          <p style={{ color: "#9ca3af", marginTop: "1rem" }}>
            Confirming your booking...
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>❌</div>
          <h2 style={{ color: "#ef4444", marginBottom: "0.5rem" }}>
            Booking Failed
          </h2>
          <p
            style={{
              color: "#9ca3af",
              fontSize: "14px",
              marginBottom: "1.5rem",
            }}
          >
            {errorMsg}
          </p>
          <button
            style={styles.btnSecondary}
            onClick={() => navigate("/dashboard/student/teachers")}
          >
            Back to Teachers
          </button>
        </div>
      </div>
    );
  }

  // success or duplicate — both show booking details
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{ fontSize: "3rem", marginBottom: "0.75rem" }}>✅</div>
          <h2 style={{ color: "#22c55e", margin: 0, fontSize: "1.4rem" }}>
            {status === "duplicate"
              ? "Already Confirmed!"
              : "Booking Confirmed!"}
          </h2>
          <p style={{ color: "#6b7280", fontSize: "13px", marginTop: "6px" }}>
            {status === "duplicate"
              ? "This session was already booked."
              : "Payment received. Your lesson is confirmed."}
          </p>
        </div>

        {/* Booking details */}
        {booking && (
          <div style={styles.detailsBox}>
            <DetailRow
              label="Date"
              value={booking.date?.split("T")[0] || "N/A"}
            />
            <DetailRow label="Time" value={formatTime(booking.time)} />
            <DetailRow label="Duration" value={`${booking.duration} minutes`} />
            <DetailRow label="Status" value={booking.status} highlight />
          </div>
        )}

        {/* Zoom Join Link */}
        {booking?.zoomJoinUrl ? (
          <a
            href={booking.zoomJoinUrl}
            target="_blank"
            rel="noreferrer"
            style={styles.zoomBtn}
          >
            🎥 Join Zoom Meeting
          </a>
        ) : (
          <div style={styles.noZoom}>
            <p style={{ margin: 0, color: "#6b7280", fontSize: "13px" }}>
              ⚠️ Zoom link not available yet. Check your upcoming sessions
              shortly.
            </p>
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: "10px", marginTop: "1rem" }}>
          <button
            style={styles.btnPrimary}
            onClick={() => navigate("/dashboard/student/upcoming")}
          >
            View My Sessions
          </button>
          <button
            style={styles.btnSecondary}
            onClick={() => navigate("/dashboard/student/teachers")}
          >
            Book Another
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- Helpers ----

const formatTime = (time) => {
  if (!time) return "N/A";
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const DetailRow = ({ label, value, highlight }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "10px 0",
      borderBottom: "1px solid #1f1f1f",
    }}
  >
    <span style={{ color: "#9ca3af", fontSize: "13px" }}>{label}</span>
    <span
      style={{
        color: highlight ? "#22c55e" : "#fff",
        fontSize: "13px",
        fontWeight: highlight ? 600 : 400,
        textTransform: highlight ? "capitalize" : "none",
      }}
    >
      {value}
    </span>
  </div>
);

// ---- Styles ----

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
    background: "#0a0a0a",
  },
  card: {
    background: "#111",
    border: "1px solid #222",
    borderRadius: "16px",
    padding: "2rem",
    maxWidth: "480px",
    width: "100%",
  },
  detailsBox: {
    background: "#0d0d0d",
    border: "1px solid #1f1f1f",
    borderRadius: "10px",
    padding: "0 1rem",
    marginBottom: "1.5rem",
  },
  zoomBtn: {
    display: "block",
    textAlign: "center",
    padding: "14px",
    borderRadius: "10px",
    background: "#2563eb",
    color: "#fff",
    fontWeight: 600,
    textDecoration: "none",
    fontSize: "15px",
    marginBottom: "0.75rem",
    transition: "opacity 0.15s",
  },
  noZoom: {
    padding: "12px",
    borderRadius: "10px",
    background: "#1a1a1a",
    border: "1px solid #333",
    marginBottom: "0.75rem",
    textAlign: "center",
  },
  btnPrimary: {
    flex: 1,
    padding: "12px",
    borderRadius: "8px",
    background: "#e6be5d",
    color: "#000",
    border: "none",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "13px",
  },
  btnSecondary: {
    flex: 1,
    padding: "12px",
    borderRadius: "8px",
    background: "#1a1a1a",
    color: "#9ca3af",
    border: "1px solid #333",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "13px",
  },
  spinner: {
    width: "36px",
    height: "36px",
    border: "3px solid #333",
    borderTop: "3px solid #e6be5d",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    margin: "0 auto",
  },
};

export default BookingSuccess;
